import { useTranslation } from '../../../i18n';
import { formatMoney, PLATFORM_CURRENCY } from '../../../utils/money';
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Card, Collapse, Form, Modal, Popconfirm, Progress, Radio, Segmented, Select, Space, Tag, Tooltip, Typography } from 'antd';
import {
    CheckCircleOutlined, CloseCircleOutlined, DeleteOutlined, LockOutlined, PlayCircleOutlined, ReloadOutlined, RollbackOutlined,
    SafetyCertificateOutlined,
} from '@ant-design/icons';
import CompactTable from './CompactTable';
import { formatDateTime } from '../../../utils/date';
import { formatBytes } from '../../../utils/format';
import { userPath } from '../../../config/paths';
import { ok, runAction } from './utils';

const { Text } = Typography;

const ACTIVE = ['queued', 'running', 'cancelling'];
const CONS = { application: ['App-consistent', 'green'], crash: ['Crash-consistent', 'orange'], offline: ['Shut down', 'blue'], quiesced: ['Consistent snapshot', 'green'] };
const SOURCE = { plan: 'Plan', addon: 'Add-on', admin: 'Admin', all: 'Plan' };
const STATUS = { queued: ['Queued', 'default'], running: ['Running', 'processing'], cancelling: ['Cancelling', 'warning'], success: ['Success', 'success'], failed: ['Failed', 'error'], cancelled: ['Canceled', 'default'] };
const bytes = (v) => (v ? formatBytes(Number(v)) : '-');

/**
 * Scheduled encrypted backups of one VM (restic, off-site). Shared by user and
 * admin VM detail: data comes through adapter.getBackups, actions through api.*
 * (user → /api/evm/user/vms/{id}/backup-*, admin → /api/evm/admin/backups/*).
 * resType="container": 独享容器（单 rootfs，整体恢复，无 VM 附加服务）。
 */
const AutoBackupCard = ({ id, api, adapter, isMobile, disabled, active, onInvoice, resType = 'vm' }) => {
    const { t } = useTranslation();
    const isAdmin = !!adapter.isAdmin;
    const isCtr = resType === 'container';
    const [plan, setPlan] = useState(null);
    const [points, setPoints] = useState([]);
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [unavailable, setUnavailable] = useState(null);
    const [restore, setRestore] = useState(null);
    const [busy, setBusy] = useState(false);
    const [pick, setPick] = useState(null);
    const [form] = Form.useForm();
    const mode = Form.useWatch('mode', form);

    const load = useCallback(async (silent) => {
        if (!silent) setLoading(true);
        try {
            const res = await adapter.getBackups(id);
            if (ok(res)) {
                setPlan(res.data.plan || {});
                setPoints(res.data.items || []);
                setJobs(res.data.jobs || []);
                setUnavailable(null);
            } else if (!silent) {
                setUnavailable(res?.msg || res?.error || t('Failed to load'));
            }
        } finally {
            if (!silent) setLoading(false);
        }
    }, [adapter, id, t]);

    useEffect(() => { if (active) load(); }, [active, load]);

    const running = plan?.running && ACTIVE.includes(plan.running.status) ? plan.running : null;
    useEffect(() => {
        if (!active || !running) return undefined;
        const iv = setInterval(() => load(true), 5000);
        return () => clearInterval(iv);
    }, [active, running, load]);

    if (unavailable && !plan) {
        return <Card size="small" title={<Space size={6}>{t('Auto backup')}<LockOutlined /></Space>}><Text type="secondary">{unavailable}</Text></Card>;
    }

    const pol = plan?.policy;
    const enabled = !!plan?.enabled;
    const manualLeft = plan?.manual_left;
    const limited = manualLeft !== null && manualLeft !== undefined;
    const canRestore = isAdmin || !!pol?.user_restore;
    const addon = plan?.addon;
    const quota = plan?.quota;
    const packs = addon?.packs || [];
    const cur = addon?.currency || PLATFORM_CURRENCY;
    const sel = packs.find((p) => p.points === pick) || packs.find((p) => p.points === addon?.points) || packs[0];
    const canBuy = !isAdmin && !isCtr && !!plan && !!addon?.enabled && !addon?.locked && packs.length > 0;

    const runNow = async () => {
        if (await runAction(() => api.runBackupNow(id), t('Backup task submitted'), t('Backup failed'))) load(true);
    };
    const del = async (p) => {
        if (await runAction(() => api.deleteBackupPoint(id, p.id), t('Deletion submitted'), t('Deletion failed'))) load(true);
    };
    const verify = async (p) => { await runAction(() => api.verifyBackupPoint(p.id), t('Restore test submitted'), t('Submission failed')); };
    const buy = (pack) => Modal.confirm({
        title: `${t('Extra restore points')} +${pack.points}`,
        content: (
            <Space direction="vertical" size={2}>
                <Text>{t('{{price}}/mo', { price: formatMoney(pack.price_month, cur) })}</Text>
                <Text strong>{pack.amount > 0 ? t('Pay now: {{amount}}', { amount: formatMoney(pack.amount, cur) }) : t('No charge now')}</Text>
            </Space>
        ),
        okText: t('Confirm'),
        cancelText: t('Cancel'),
        onOk: async () => {
            setBusy(true);
            try {
                const res = await runAction(() => api.buyBackupAddon(id, pack.points), t('Order created'), t('Purchase failed'));
                if (!res) return;
                if (res.data?.paid) load();
                else if (res.data?.invoice_id && onInvoice) onInvoice(res.data.invoice_id);
            } finally {
                setBusy(false);
            }
        },
    });
    const cancelAddon = async () => {
        if (await runAction(() => api.cancelBackupAddon(id), t('Canceled'), t('Cancellation failed'))) load(true);
    };
    const openRestore = (p) => setRestore(p);
    const submitRestore = async () => {
        const v = await form.validateFields();
        Modal.confirm({
            title: isCtr ? t('This will overwrite the current container data') : t('This will overwrite the current disk data'),
            okText: t('Restore'),
            okButtonProps: { danger: true },
            cancelText: t('Cancel'),
            onOk: async () => {
                const body = { dev: !isCtr && v.mode === 'disk' ? v.dev : '', power_on: v.power_on || 'keep' };
                if (await runAction(() => api.restoreBackupPoint(id, restore.id, body), t('Restore task submitted'), t('Restore failed'))) {
                    setRestore(null);
                    load(true);
                }
            },
        });
    };

    const retention = pol ? [
        pol.keep_daily ? t('{{n}} days', { n: pol.keep_daily }) : null,
        pol.keep_weekly ? t('{{n}} weeks', { n: pol.keep_weekly }) : null,
        pol.keep_monthly ? t('{{n}} months', { n: pol.keep_monthly }) : null,
    ].filter(Boolean).join(' / ') : '';

    const columns = [
        { title: t('Time'), key: 'created_at', render: (_, p) => formatDateTime(p.created_at) },
        { title: t('Type'), key: 'kind', render: (_, p) => <Tag color={p.kind === 'manual' ? 'purple' : 'default'}>{p.kind === 'manual' ? t('Manual') : t('Scheduled')}</Tag> },
        {
            title: t('Consistency'), key: 'consistency', render: (_, p) => {
                const c = CONS[p.consistency];
                return c ? <Tag color={c[1]}>{t(c[0])}</Tag> : '-';
            },
        },
        { title: t('Size'), key: 'size', render: (_, p) => <Tooltip title={(p.disks || []).map((d) => `${d.dev}: ${bytes(d.bytes)}`).join(' / ')}>{bytes(p.total_bytes)}</Tooltip> },
        {
            title: t('Verified'), key: 'verify', render: (_, p) => {
                if (p.verify_ok === true) return <Tooltip title={formatDateTime(p.verified_at)}><CheckCircleOutlined style={{ color: '#52c41a' }} /></Tooltip>;
                if (p.verify_ok === false) return <Tooltip title={p.verify_msg || formatDateTime(p.verified_at)}><CloseCircleOutlined style={{ color: '#ff4d4f' }} /></Tooltip>;
                return '-';
            },
        },
        {
            title: '', key: 'actions', align: 'right', render: (_, p) => (
                <Space size={4}>
                    {canRestore && (
                        <Button size="small" type="text" icon={<RollbackOutlined />} disabled={disabled || !!running} onClick={() => openRestore(p)}>{t('Restore')}</Button>
                    )}
                    {isAdmin && (
                        <Tooltip title={t('Checks it can be restored; writes nothing')}>
                            <Button size="small" type="text" icon={<SafetyCertificateOutlined />} onClick={() => verify(p)}>{t('Dry run')}</Button>
                        </Tooltip>
                    )}
                    {(isAdmin || p.kind === 'manual') && (
                        <Popconfirm title={t('Delete this backup point?')} okButtonProps={{ danger: true }} onConfirm={() => del(p)} disabled={disabled}>
                            <Button size="small" type="text" danger icon={<DeleteOutlined />} disabled={disabled}>{t('Delete')}</Button>
                        </Popconfirm>
                    )}
                </Space>
            ),
        },
    ];

    const jobColumns = [
        { title: t('Time'), key: 'created_at', render: (_, j) => formatDateTime(j.created_at) },
        { title: t('Type'), key: 'kind', render: (_, j) => (j.kind === 'restore' ? t('Restore') : j.kind === 'backup' ? t('Backups') : j.kind) },
        {
            title: t('Status'), key: 'status', render: (_, j) => {
                const [label, color] = STATUS[j.status] || [j.status, 'default'];
                const tag = <Tag color={color}>{t(label)}</Tag>;
                return j.error ? <Tooltip title={j.error}>{tag}</Tooltip> : tag;
            },
        },
        { title: t('Added'), key: 'added', render: (_, j) => bytes(j.bytes_added) },
    ];

    const runTip = !enabled ? undefined
        : running ? t('A job is in progress')
            : limited ? t('{{n}} left today', { n: manualLeft }) : undefined;

    return (
        <Card
            size="small"
            title={(
                <Space size={6}>
                    {t('Auto backup')}
                    <Tooltip title={t('Encrypted and stored off-site')}><LockOutlined style={{ fontSize: 12 }} /></Tooltip>
                </Space>
            )}
            extra={(
                <Space size={4}>
                    <Button size="small" type="text" icon={<ReloadOutlined />} onClick={() => load()} loading={loading} />
                    {enabled && (
                        <Tooltip title={runTip}>
                            <Popconfirm title={t('Back up now?')} onConfirm={runNow} disabled={disabled || !!running || (limited && manualLeft <= 0)}>
                                <Button size="small" type="primary" icon={<PlayCircleOutlined />} disabled={disabled || !!running || (limited && manualLeft <= 0)}>
                                    {t('Back up now')}
                                </Button>
                            </Popconfirm>
                        </Tooltip>
                    )}
                </Space>
            )}
        >
            <Space direction="vertical" size={10} style={{ width: '100%' }}>
                <Space size={[8, 4]} wrap>
                    {enabled ? <Tag color="green">{t('Enabled')}</Tag> : <Tag>{plan?.source === 'admin_off' ? t('Closed') : t('Not activated')}</Tag>}
                    {enabled && plan?.source && SOURCE[plan.source] && <Tag color="blue">{t(SOURCE[plan.source])}</Tag>}
                    {enabled && pol && <Text type="secondary">{t('Every {{n}} h', { n: pol.interval_hours })}</Text>}
                    {enabled && retention && <Text type="secondary">{t('Keep')} {retention}</Text>}
                    {enabled && quota && (
                        <Tooltip title={t('Included {{a}} + extra {{b}}', { a: quota.included, b: quota.extra })}>
                            <Text type="secondary">{t('Restore points')}: {quota.used} / {quota.total}</Text>
                        </Tooltip>
                    )}
                    {enabled && <Text type="secondary">{t('Last success')}: {plan?.last_success_at ? formatDateTime(plan.last_success_at) : t('Never')}</Text>}
                    {isAdmin && plan?.target && <Text type="secondary">{t('Storage target')}: {plan.target.name} ({plan.target.type})</Text>}
                </Space>

                {running && (
                    <Space size={8} style={{ width: '100%' }}>
                        <Text>{running.kind === 'restore' ? t('Restoring') : t('Backing up')}</Text>
                        <Progress size="small" style={{ width: 220, margin: 0 }} percent={Math.round(Number(running.progress) || 0)} status="active" />
                    </Space>
                )}

                {!enabled && !isAdmin && plan && isCtr && plan.source !== 'admin_off' && (plan.addon_subscription_id ? (
                    <Text type="secondary">
                        <Link to={userPath(`/billing/subscription?id=${plan.addon_subscription_id}`)}>{t('Buy the "Auto Backup" add-on on the subscription page to enable')}</Link>
                    </Text>
                ) : <Text type="secondary">{t('Your plan doesn\'t include auto backup')}</Text>)}

                {canBuy && sel && (
                    <Space size={[8, 4]} wrap>
                        <Text type="secondary">{t('Extra restore points')}</Text>
                        <Segmented size="small" value={sel.points} onChange={setPick} options={packs.map((p) => ({ value: p.points, label: `+${p.points}` }))} />
                        <Text>{t('{{price}}/mo', { price: formatMoney(sel.price_month, cur) })}</Text>
                        {sel.action !== 'current' && (
                            <Button size="small" type="primary" loading={busy} disabled={disabled} onClick={() => buy(sel)}>{addon.active ? t('Change') : t('Buy')}</Button>
                        )}
                        {addon.active && (
                            <Popconfirm title={t('Cancel the auto backup add-on?')} onConfirm={cancelAddon}>
                                <Button type="link" size="small" style={{ padding: 0 }}>{t('Cancel')}</Button>
                            </Popconfirm>
                        )}
                    </Space>
                )}

                {!enabled && !isAdmin && !isCtr && plan && !canBuy && plan.source !== 'admin_off' && <Text type="secondary">{t('Your plan doesn\'t include auto backup')}</Text>}

                {(enabled || points.length > 0) && (
                    <CompactTable isMobile={isMobile} columns={columns} dataSource={points} loading={loading} emptyText={t('No backups yet')} />
                )}

                {jobs.length > 0 && (
                    <Collapse ghost size="small" items={[{
                        key: 'jobs', label: t('Recent jobs'),
                        children: <CompactTable isMobile={isMobile} columns={jobColumns} dataSource={jobs} emptyText={t('No jobs')} />,
                    }]} />
                )}
            </Space>

            <Modal title={t('Restore from backup')} open={!!restore} onCancel={() => setRestore(null)} onOk={submitRestore} okText={t('Restore')} okButtonProps={{ danger: true }} destroyOnClose>
                <Form form={form} layout="vertical" initialValues={{ mode: 'all', dev: restore?.disks?.[0]?.dev, power_on: 'keep' }}>
                    {isCtr && <Form.Item><Text>{formatDateTime(restore?.created_at)} · {bytes(restore?.total_bytes)}</Text></Form.Item>}
                    {!isCtr && <Form.Item name="mode">
                        <Radio.Group optionType="button" options={[{ value: 'all', label: t('Whole VM') }, { value: 'disk', label: t('Single disk') }]} />
                    </Form.Item>}
                    {!isCtr && mode === 'disk' && (
                        <Form.Item name="dev" label={t('Disk')} rules={[{ required: true, message: t('Select a disk') }]}>
                            <Select options={(restore?.disks || []).map((d) => ({ value: d.dev, label: `${d.dev} · ${bytes(d.bytes)}` }))} />
                        </Form.Item>
                    )}
                    <Collapse ghost size="small" items={[{
                        key: 'adv', label: t('Advanced'), children: (
                            <Form.Item name="power_on" label={t('Power after restore')} style={{ marginBottom: 0 }}>
                                <Segmented options={[{ value: 'keep', label: t('Keep') }, { value: 'on', label: t('Power on') }, { value: 'off', label: t('Shut down') }]} />
                            </Form.Item>
                        ),
                    }]} />
                </Form>
            </Modal>
        </Card>
    );
};

export default AutoBackupCard;
